import {focusElement, hookToAttributeSelector} from '@wix/panda-js-utils'
import {ExperimentNames, getFormMessages, isRsvp} from '@wix/wix-events-commons-statics'
import {detailsPageDataHooks as DH} from '@wix/wix-events-data-hooks'
import {useExperiments, useTranslation} from '@wix/yoshi-flow-editor'
import classNames from 'classnames'
import React, {useEffect, useState} from 'react'
import {SectionNotification} from 'wix-ui-tpa/cssVars'
import {FormStep} from '../../constants'
import {useAppActions, useAppSelector} from '../../hooks/runtime'
import {getEvent} from '../../selectors/event'
import {getInvoice} from '../../selectors/placed-order'
import {getPolicies, hasAgreedWithPolicies, shouldShowAlert} from '../../selectors/policies'
import c from '../classnames.scss'
import {FormSubmitButton} from '../form-submit-button'
import {PoliciesProps} from './interfaces'
import s from './policies.scss'
import {PolicyCheckbox} from './policy-checkbox'

export const Policies = ({editing, onSubmit}: PoliciesProps) => {
  const {experiments} = useExperiments()
  const useReduxToolkitEnabled = experiments.enabled(ExperimentNames.UseDetailsPageReduxToolkit)

  const {t} = useTranslation()
  const {toggleAgreePolicies, toggleAgreePoliciesOld, openPolicyModal} = useAppActions()
  const [clickedSubmit, setClickedSubmit] = useState(false)

  const agreedWithPolicies = useAppSelector(hasAgreedWithPolicies)
  const policies = useAppSelector(getPolicies)
  const alertVisible = useAppSelector(shouldShowAlert)
  const event = useAppSelector(getEvent)
  const invoice = useAppSelector(getInvoice)

  const messages = getFormMessages(event)
  const rsvp = isRsvp(event)
  const needsToPay = Number(invoice.grandTotal.amount) > 0

  useEffect(() => {
    focusElement({selector: hookToAttributeSelector(DH.CHECKOUT_STEP(FormStep.Policies))})
  }, [])

  const submit = () => {
    setClickedSubmit(true)

    if (agreedWithPolicies) {
      onSubmit()
    }
  }

  const onPolicyClick = (event: React.MouseEvent<HTMLAnchorElement>, id: string, returnFocusElement) => {
    event.stopPropagation()
    openPolicyModal({policyId: id, returnFocusElement})
  }

  const buttonText = needsToPay
    ? t('policies_continueToPayment')
    : rsvp
    ? messages.rsvp.submitActionLabel
    : messages.checkout.submitActionLabel

  return editing ? (
    <div className={s.container}>
      <div className={classNames(c.formLabelsColor, c.formLabelsFont, s.description)}>{t('policies_description')}</div>
      <div className={classNames(s.checkboxContainer, {[s.alert]: alertVisible})}>
        <PolicyCheckbox
          label={t('policies_agreement')}
          value="policies"
          checked={agreedWithPolicies}
          onChange={() => (useReduxToolkitEnabled ? toggleAgreePolicies() : toggleAgreePoliciesOld())}
          policies={policies}
          onPolicyClick={onPolicyClick}
          errorShown={clickedSubmit && !agreedWithPolicies}
        />
      </div>
      {alertVisible && (
        <div className={s.sectionNotificationContainer}>
          <SectionNotification type="alert">{t('policies_updatedAlert')}</SectionNotification>
        </div>
      )}
      <FormSubmitButton dataHook={DH.FORM_BUTTON} text={buttonText} onClick={submit} />
    </div>
  ) : null
}
